/**
 * NotificationMentionItem
 *
 * Covers the two mention types whose subtitle is just the notification date:
 *   - contentMention — `notification_content_mention_item.dart:37-42` (photo/video)
 *   - commentMention — `notification_comment_mention_item.dart:36-37`
 *
 * Avatar from `usersInfo.userId`, trailing thumbnail from `mediaInfo`.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { NotificationMentionKind } from '@/domain/enums/inbox/notificationMentionKind';
import { NotificationRowLayout } from '../NotificationRowLayout';
import { NotificationAvatar } from './shared/NotificationAvatar';
import { NotificationMediaThumbnail } from './shared/NotificationMediaThumbnail';
import type { NotificationItemProps } from './notificationItemProps';

interface NotificationMentionItemProps extends NotificationItemProps {
  kind: NotificationMentionKind;
}

export const NotificationMentionItem: React.FC<NotificationMentionItemProps> = ({
  kind,
  notification,
  onUserTap,
  onItemTap,
  onItemMediaTap,
}) => {
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();
  const n = l10n.inbox.notifications;

  const { info } = notification;
  const mediaInfo = info.mediaInfo;
  const name = info.usersInfo.name;
  const isVideo = MediaUtils.mediaInfoIsVideo(mediaInfo);

  const title = (
    kind === NotificationMentionKind.commentMention
      ? n.mentionedYouInComment
      : isVideo
        ? n.mentionedYouInVideo
        : n.mentionedYouInPhoto
  ).replace('{name}', name);

  return (
    <NotificationRowLayout
      leading={<NotificationAvatar userId={info.usersInfo.userId} alt={name} />}
      title={title}
      subtitle={formatDate(info.timestamp)}
      trailing={mediaInfo ? <NotificationMediaThumbnail mediaInfo={mediaInfo} /> : undefined}
      onItemTap={onItemTap ? () => onItemTap(notification) : undefined}
      onLeadingTap={onUserTap ? () => onUserTap(info.usersInfo.userId) : undefined}
      onTrailingTap={onItemMediaTap ? () => onItemMediaTap(notification) : undefined}
    />
  );
};
